import { HTMLProps, useMemo, useState } from 'react';
import cx from 'classnames';
import { useDropzone } from 'react-dropzone';

import { IMAGE_SIZE_LIMIT } from 'utils';
import { CloseNewIcon, DeleteIcon, RotateIcon, ThreeDotsIcon, UploadIcon } from 'components/Icon';

import styles from './ImageUpload.module.scss';

export type ImageUploadProps = Pick<HTMLProps<HTMLDivElement>, 'className'> & {
  actions?: ImageUploadAction[];
  invalid?: boolean;
  feedback?: string;
  value: File | null;
  maxSize?: number;
  defaultValue?: string | null;
  onChange?(acceptedFile: File | null): void;
};

export type ImageUploadAction = 'reset' | 'update';

const ImageUpload = ({
  invalid,
  feedback,
  className,
  maxSize = IMAGE_SIZE_LIMIT,
  actions = ['reset', 'update'],
  defaultValue,
  value,
  onChange
}: ImageUploadProps) => {
  const { getRootProps, getInputProps, open } = useDropzone({
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: {
      'image/*': ['.jpeg', '.png']
    },
    onDrop: acceptedFiles => {
      onChange?.(acceptedFiles[0]);
    }
  });
  const [actionsVisible, setActionsVisible] = useState(false);
  const [reset, setReset] = useState(false);

  const previewURL = useMemo(
    () => (value ? window.URL.createObjectURL(value) : !reset && defaultValue),
    [reset, value, defaultValue]
  );

  const handleOnToggleActionsVisibility = () => setActionsVisible(prev => !prev);

  const handleOnReset = () => {
    setReset(true);
    onChange?.(null);
  };

  const handleOnUpdate = () => {
    console.log('handleOnUpdate');
    open();
  };

  const renderAction = (action: ImageUploadAction) => {
    switch (action) {
      case 'reset': {
        return (
          <button type="button" className={styles.option} onClick={handleOnReset}>
            <DeleteIcon />
          </button>
        );
      }

      case 'update': {
        return (
          <button type="button" className={styles.option} onClick={handleOnUpdate}>
            <RotateIcon />
          </button>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className={cx(styles.container, { [styles.invalid]: invalid }, className)}>
      <div className={styles.content}>
        {previewURL ? (
          <div className={styles.preview}>
            <img className={styles.cover} alt="poster" src={previewURL} />
            <img className={styles.image} alt="poster" src={previewURL} />
            <div className={cx(styles.actions, { [styles.open]: actionsVisible })}>
              <button type="button" className={styles.toggle} onClick={handleOnToggleActionsVisibility}>
                {actionsVisible ? <CloseNewIcon /> : <ThreeDotsIcon />}
              </button>
              <ul className={styles.options}>
                {actions.map(action => (
                  <li key={action}>{renderAction(action)}</li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div {...getRootProps({ className: styles.drag })}>
            <input {...getInputProps()} />
            <UploadIcon />
            <h3>Upload a cover photo</h3>
            <span>Max: {`${maxSize / 1024}KB`}</span>
          </div>
        )}
      </div>
      {feedback && <span className={styles.feedback}>{feedback}</span>}
    </div>
  );
};

export default ImageUpload;
